import { Box, Flex, Text } from '@chakra-ui/react';
import { Container, DisplayHeading, Eyebrow, PhoneFrame, Postmark, Stamp } from '@/travo/components/ui/postmark';
import { HARD_SHADOW } from '@/travo/theme/constants';
import { useCopy } from '@/travo/i18n';
import { cycle } from '@/travo/utils/cycle';
import checkinsScreenshot from '@/travo/assets/images/home/previews/app-daily-highlights.webp';

/** app-daily-highlights.webp is 640×1344 (cropped above the app's test ad); update with the image. */
const CHECKINS_SCREENSHOT_RATIO = '640 / 1344';

interface CheckinStampArt {
  /** Theme color token inside the stamp frame. */
  fill: string;
  tilt: string;
}

/** Per-stamp art, in the same order as copy.checkins.stamps. */
const STAMP_ART: [CheckinStampArt, ...CheckinStampArt[]] = [
  { fill: 'sun', tilt: '-4deg' },
  { fill: 'sky', tilt: '2deg' },
  { fill: 'sand', tilt: '-1deg' }
];

/** Dark band: up to three check-ins a day, drawn as three stamps. */
const CheckIns = () => {
  const copy = useCopy();

  return (
    <Box as="section" bg="ink" color="paper" mt="48px">
      <Container py={{ base: '80px', md: '104px' }}>
        <Flex wrap="wrap" align="center" gap="64px">
          <Flex direction="column" gap="28px" flex="1 1 480px" minW={0}>
            <Eyebrow color="sun">{copy.checkins.eyebrow}</Eyebrow>
            <DisplayHeading fontSize={{ base: '38px', md: '62px' }} lineHeight="1.1">
              {copy.checkins.title}
            </DisplayHeading>
            <Text maxW="30em" color="sand">
              {copy.checkins.body}
            </Text>
            <Flex wrap="wrap" gap="18px">
              {copy.checkins.stamps.map((label, i) => (
                <Stamp
                  key={label}
                  w="150px"
                  edge="paper"
                  frame={cycle(STAMP_ART, i).fill}
                  margin="12px"
                  innerPadding="14px 12px"
                  transform={`rotate(${cycle(STAMP_ART, i).tilt})`}
                  filter="none"
                >
                  <Flex direction="column" justify="space-between" h="82px" color="ink">
                    <Text fontFamily="heading" fontSize="30px" lineHeight="1">
                      {String(i + 1).padStart(2, '0')}
                    </Text>
                    <Text fontWeight="700" fontSize="15px" lineHeight="1.3">
                      {label}
                    </Text>
                  </Flex>
                </Stamp>
              ))}
            </Flex>
          </Flex>

          <Flex flex="1 1 360px" minW={0} justify="center" position="relative">
            <PhoneFrame
              src={checkinsScreenshot}
              alt={copy.checkins.screenshotAlt}
              ratio={CHECKINS_SCREENSHOT_RATIO}
              w="300px"
              maxW="100%"
              // the ink bezel would vanish on this ink section; paper/40 keeps the edge at ≥3:1
              outline="2px solid {colors.paper/40}"
              filter={HARD_SHADOW.phone}
            />
            <Postmark
              ring="CHECKED IN · GOÐAFOSS · ICELAND ·"
              big="1/3"
              position="absolute"
              right="2%"
              bottom="40px"
              w={{ base: '120px', md: '150px' }}
              h={{ base: '120px', md: '150px' }}
              color="sun"
              transform="rotate(12deg)"
            />
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
};

export default CheckIns;
